/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import Loading from '../../../components/Loading';
import { getProductivityReport } from '../../../services/routing/routingApi';

import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../../styles/layout/PageHeader';
import {
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';

import {
   formatDayLong,
   getCurrentMonthRange,
   isValidIsoDate,
} from './helpers';
import {
   EmptyState,
   FiltersSection,
   SearchButton,
   TabBar,
   TabButton,
} from './styles';

// Cada aba é importada via React.lazy: o JS (incluindo recharts) só baixa
// quando a aba é ativada pela primeira vez. Combinado com a renderização
// condicional (`activeTab === 'X' && <Tab />`), abas inativas não consomem
// CPU nem mantêm estado em memória.
const OverviewTab = lazy(() => import('./tabs/OverviewTab'));
const TechniciansTab = lazy(() => import('./tabs/TechniciansTab'));
const DailyTab = lazy(() => import('./tabs/DailyTab'));
const ServicesTab = lazy(() => import('./tabs/ServicesTab'));
const UnproductiveTab = lazy(() => import('./tabs/UnproductiveTab'));
const IndividualReportTab = lazy(() => import('./tabs/IndividualReportTab'));

const TABS = [
   { key: 'overview',     label: 'Visão geral do mês' },
   { key: 'technicians',  label: 'Produtividade dos técnicos' },
   { key: 'daily',        label: 'Dia a dia' },
   { key: 'services',     label: 'Ranking de serviços' },
   { key: 'unproductive', label: 'Improdutividade' },
   { key: 'individual',   label: 'Relatório individual' },
];

const DEFAULT_TAB = 'overview';
const VALID_TABS = new Set(TABS.map((t) => t.key));

export default function ProductivityReport() {
   const permissions = useSelector(
      (state) => state.auth.user?.permissions || [],
   );
   const canView = permissions.includes('routing:report:read');

   // ─── Aba ativa via query string ─────────────────────────────────────
   const [searchParams, setSearchParams] = useSearchParams();
   const tabParam = searchParams.get('tab');
   const activeTab = VALID_TABS.has(tabParam) ? tabParam : DEFAULT_TAB;

   const handleTabChange = (key) => {
      const next = new URLSearchParams(searchParams);
      if (key === DEFAULT_TAB) {
         next.delete('tab');
      } else {
         next.set('tab', key);
      }
      setSearchParams(next, { replace: false });
   };

   // ─── Filtros e fetch ────────────────────────────────────────────────
   const initialRange = useMemo(getCurrentMonthRange, []);
   const [dateFrom, setDateFrom] = useState(initialRange.dateFrom);
   const [dateTo, setDateTo] = useState(initialRange.dateTo);

   const [report, setReport] = useState(null);
   const [isLoading, setIsLoading] = useState(false);
   const [appliedRange, setAppliedRange] = useState(null);

   const fetchReport = useCallback(async (from, to) => {
      if (!isValidIsoDate(from) || !isValidIsoDate(to)) {
         toast.warn('Informe datas válidas (AAAA-MM-DD).');
         return;
      }
      if (from > to) {
         toast.warn('A data inicial não pode ser maior que a final.');
         return;
      }
      setIsLoading(true);
      try {
         const result = await getProductivityReport({
            dateFrom: from,
            dateTo: to,
         });
         setReport(result);
         setAppliedRange({ from, to });
      } catch (err) {
         const msg =
            err?.response?.data?.message ??
            'Erro ao carregar o relatório de produtividade.';
         toast.error(msg);
      } finally {
         setIsLoading(false);
      }
   }, []);

   // Busca inicial automática (mês corrente até hoje).
   useEffect(() => {
      if (!canView) return;
      fetchReport(initialRange.dateFrom, initialRange.dateTo);
   }, [canView, initialRange.dateFrom, initialRange.dateTo, fetchReport]);

   if (!canView) {
      return (
         <Container>
            <PageHeader>
               <PageHeaderText>
                  <PageTitle>Dashboard de Produtividade</PageTitle>
                  <PageSubtitle>Roteirização</PageSubtitle>
               </PageHeaderText>
            </PageHeader>
            <EmptyState>
               <span style={{ fontSize: 32 }}>🔒</span>
               <span>Você não tem permissão para acessar este dashboard.</span>
            </EmptyState>
         </Container>
      );
   }

   const summary = report?.summary ?? null;
   const rankings = report?.technicianRankings ?? [];
   const dailySeries = report?.dailySeries ?? [];

   return (
      <Container $size="xl">
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Dashboard de Produtividade</PageTitle>
               <PageSubtitle>
                  {appliedRange
                     ? `${formatDayLong(appliedRange.from)} até ${formatDayLong(appliedRange.to)}`
                     : 'Roteirização — visão geral do mês'}
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         {/* ─── Filtros (sticky) ─────────────────────────────────── */}
         <FiltersSection>
            <SectionHeader>
               <SectionTitle>Período</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <FormGrid>
                  <Field $col={3} $colMobile={6}>
                     <label>Data inicial</label>
                     <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                     />
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Data final</label>
                     <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                     />
                  </Field>
                  <Field $col={2} $colMobile={12} $alignEnd>
                     <label>&nbsp;</label>
                     <SearchButton
                        type="button"
                        onClick={() => fetchReport(dateFrom, dateTo)}
                        disabled={isLoading}
                     >
                        Buscar
                     </SearchButton>
                  </Field>
               </FormGrid>
            </SectionBody>
         </FiltersSection>

         {/* ─── Barra de abas ────────────────────────────────────── */}
         <TabBar>
            {TABS.map((t) => (
               <TabButton
                  key={t.key}
                  type="button"
                  $active={activeTab === t.key}
                  onClick={() => handleTabChange(t.key)}
               >
                  {t.label}
               </TabButton>
            ))}
         </TabBar>

         {/* ─── Conteúdo da aba ativa (lazy) ──────────────────────── */}
         {!report ? (
            <EmptyState>
               <span style={{ fontSize: 28 }}>📊</span>
               <span>Selecione um período acima e clique em Buscar.</span>
            </EmptyState>
         ) : (
            <Suspense fallback={<Loading isLoading />}>
               {activeTab === 'overview' && summary && (
                  <OverviewTab summary={summary} />
               )}
               {activeTab === 'technicians' && (
                  <TechniciansTab rankings={rankings} />
               )}
               {activeTab === 'daily' && <DailyTab dailySeries={dailySeries} />}
               {activeTab === 'services' && <ServicesTab rankings={rankings} />}
               {activeTab === 'unproductive' && summary && (
                  <UnproductiveTab summary={summary} rankings={rankings} />
               )}
               {activeTab === 'individual' && (
                  <IndividualReportTab rankings={rankings} />
               )}
            </Suspense>
         )}
      </Container>
   );
}
